/**
 * HeroModeField — picker UI for the automatic hero mode.
 *
 * Why this layout
 * ===============
 *
 * The previous "family grid + inline expand tray" UX shipped two
 * problems users ran into:
 *
 *   1. Clicking a multi-member family only **opened** the tray —
 *      no selection happened until the user picked a variant. That
 *      double-click flow was easy to miss; users would click a
 *      family, see nothing change in their hero, and assume the
 *      picker was broken.
 *
 *   2. The tray expanded *inline* inside the same grid as the other
 *      family tiles, so when it opened it pushed unrelated families
 *      around and made it visually unclear which family the tray
 *      belonged to / which variant was actually selected.
 *
 * The new picker is a **two-section** layout:
 *
 *   ┌────────────────────────────────────────────────────┐
 *   │  ACTIVE FAMILY (always at the top)                 │
 *   │  Header: family label + description                │
 *   │  Row of variant tiles — selected variant is ringed │
 *   ├────────────────────────────────────────────────────┤
 *   │  OTHER LAYOUTS (scrollable region below)           │
 *   │  Grid of family cards. Click any card → that       │
 *   │  family's ``default_member`` is selected and the   │
 *   │  family swaps into the top section.                │
 *   └────────────────────────────────────────────────────┘
 *
 * Behaviour notes
 * ---------------
 *
 *   * **Auto-select on family click.** Clicking a card in the
 *     "other layouts" section *immediately* calls
 *     ``onChange(family.default_member)``. Singletons did this
 *     already; this change extends it to multi-member families so
 *     the click always produces a visible result. If the user
 *     wants a non-default variant they pick it from the top section
 *     after the family activates.
 *
 *   * **Variant selection** stays a single click in the top
 *     section (``onChange(variant.id)``). Selected variant gets a
 *     ring + check.
 *
 *   * **Initial state with no current value** falls back to a
 *     one-section grid of all family cards. There is no "active
 *     family" until the user picks one. This avoids implying a
 *     selection that doesn't exist server-side.
 *
 *   * **Singletons** activate normally — when their member is the
 *     current value the top section just shows that single tile.
 *     The bottom section still lists them as cards.
 *
 *   * **Internal scroll.** The "other layouts" section uses a
 *     ``max-height + overflow-y-auto`` so the picker as a whole
 *     stays compact in narrow side panels. The active family is
 *     always visible (sits outside the scroll region).
 *
 * Family payload contract
 * -----------------------
 * The component reads ``heroModes.modes`` and ``heroModes.families``
 * from ``MetadataContext``. Both are produced by
 * ``/api/metadata/hero-modes/`` (see ``views/infrastructure/metadata.py``).
 * If the API is missing the ``families`` key (e.g. a stale
 * deployment), we fall back to a flat one-mode-per-family grouping
 * so the picker still renders something usable rather than blank.
 *
 * Animation indicator
 * -------------------
 * Modes whose backend metadata flags ``has_motion`` get a small
 * "▶ Animated" chip in the top-right corner. The chip visually
 * differentiates passive paired-animated modes (e.g. ``card_animated``)
 * and animation-first modes (e.g. ``mosaic``) from their static
 * siblings without forcing the user to read the description.
 *
 * ``has_motion`` is the canonical metadata flag — it's ``True`` for
 * every mode that has ongoing motion, including JS-driven modes
 * (``magazine_swap`` / ``magazine_fade`` / ``spotlight_swap`` …)
 * whose ``ambient_animation`` is intentionally ``None`` (the
 * renderer coordinates the motion in JS rather than via a CSS
 * overlay class). We fall back to ``ambient_animation`` for the
 * extra safety belt of not regressing in front of an older
 * backend that didn't yet ship the ``has_motion`` field.
 */
import React, { useMemo, useCallback } from "react";
import { CheckOutlined } from "@ant-design/icons";
import { useMetadata } from "../../context/MetadataContext";
import {
  typography,
  surfaces,
  borders,
  textColors,
  interactive,
} from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";
import HeroModePreview from "./HeroModePreview";

// ---------------------------------------------------------------------------
// Animation flag resolver
// ---------------------------------------------------------------------------
//
// Single source of truth for "does this mode merit the Animated
// chip in the picker?". Centralising the rule means every tile
// (active-family variants, bottom-grid family cards, initial-state
// grid, flat fallback) reads the same signal — historically each
// site spelled it out independently and the JS-driven motion
// modes (which deliberately ship ``ambient_animation = None``,
// see ``magazine_swap`` / ``spotlight_swap``) silently dropped
// off the list at every site at once.
//
// Prefers the explicit ``has_motion`` field over the derived
// ``ambient_animation`` check; both are accepted so the picker
// keeps working against an older backend payload where
// ``has_motion`` hasn't yet been serialised.

function modeHasMotion(mode) {
  if (!mode) return false;
  if (mode.has_motion != null) return Boolean(mode.has_motion);
  return Boolean(mode.ambient_animation);
}

// ---------------------------------------------------------------------------
// Animated chip
// ---------------------------------------------------------------------------
//
// A single visual cue that reads as "this mode has continuous motion"
// regardless of which specific ambient animation the mode actually
// uses. Two flavours:
//
//   * On unselected tiles: faint blue-tinted chip with a play triangle
//     and a small "Animated" label. Subtle enough to not visually
//     compete with the preview; loud enough to spot at a glance.
//   * On selected tiles: white-tinted chip on the selected blue
//     background — same icon + label, just inverted contrast.
//
// The chip is positioned at the top-right corner of the preview frame
// (the check-mark moves to the bottom-right when both indicators are
// present; see ``HeroModeTile``).

const AnimatedChip = React.memo(({ isSelected, compact = false }) => (
  <span
    aria-label="Animated"
    className={[
      "absolute top-1 right-1 inline-flex items-center gap-0.5",
      "rounded-full pointer-events-none",
      "shadow-sm backdrop-blur-[2px]",
      compact ? "px-1 py-px" : "px-1.5 py-0.5",
      isSelected
        ? "bg-white/95 text-blue-700"
        : "bg-blue-500/90 text-white dark:bg-blue-400/90 dark:text-slate-900",
    ].join(" ")}
  >
    <svg
      viewBox="0 0 6 6"
      className={compact ? "w-1.5 h-1.5" : "w-2 h-2"}
      fill="currentColor"
      aria-hidden="true"
    >
      {/* Right-pointing play triangle */}
      <path d="M1.2 0.8 L1.2 5.2 L5 3 Z" />
    </svg>
    {!compact && (
      <span className="text-[8px] font-semibold tracking-wide leading-none uppercase">
        Animated
      </span>
    )}
  </span>
));
AnimatedChip.displayName = "AnimatedChip";

// ---------------------------------------------------------------------------
// Variant count badge
// ---------------------------------------------------------------------------
//
// Tells the user at a glance "this family has N variations" without
// needing them to switch to it first. Uses a neutral grey chip
// (not blue) so it doesn't get mistaken for the "Animated"
// indicator.

const VariantCountBadge = React.memo(({ count, isSelected }) => (
  <span
    aria-label={`${count} variants`}
    className={[
      "inline-flex items-center gap-0.5 rounded-full",
      "px-1.5 py-0.5 text-[9px] font-semibold leading-none",
      "pointer-events-none",
      isSelected
        ? "bg-white/25 text-white"
        : `${surfaces.tertiary} ${textColors.tertiary} ${borders.default} border`,
    ].join(" ")}
  >
    +{count - 1}
  </span>
));
VariantCountBadge.displayName = "VariantCountBadge";

// ---------------------------------------------------------------------------
// Single tile (used for variant tiles AND family cards)
// ---------------------------------------------------------------------------
//
// The visual unit shared between every clickable tile in the picker.
// What changes between contexts is what the click does (handled by
// the parent) and which preview is shown.
//
// Props:
//   * modeId: the mode whose preview is rendered.
//   * isSelected: render a blue ring + check mark.
//   * showAnimatedChip: render the "Animated" chip when truthy.
//   * variantCount: render a "+N" badge when > 1.
//   * size: "md" (active section) vs "sm" (variant row + bottom grid)

const HeroModeTile = React.memo(function HeroModeTile({
  modeId,
  label,
  description,
  isSelected,
  showAnimatedChip,
  variantCount,
  onClick,
  title,
  size = "md",
}) {
  const isMd = size === "md";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || description}
      aria-pressed={isSelected}
      className={[
        "group relative flex flex-col items-stretch text-left",
        "rounded-xl border transition-all overflow-hidden",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
        isMd ? "p-2" : "p-1.5",
        isSelected
          ? `${interactive.selectedBg} ${interactive.selectedBorder} shadow-md`
          : `${borders.default} ${surfaces.tertiary} ${surfaces.hover}`,
      ].join(" ")}
    >
      <div
        className={[
          "relative w-full rounded-md overflow-hidden",
          isMd ? "aspect-[3/2] mb-2" : "aspect-[3/2] mb-1.5",
          // Subtle inner gradient on the preview frame so the
          // mockups sit on a real surface (not a flat tile bg). The
          // gradient only shows behind transparent regions of the
          // SVG (e.g. the 'centered' mode's negative space).
          isSelected
            ? "bg-gradient-to-b from-blue-500/10 to-blue-500/30"
            : "bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900",
        ].join(" ")}
      >
        <HeroModePreview modeId={modeId} isSelected={isSelected} />

        {showAnimatedChip && (
          <AnimatedChip isSelected={isSelected} compact={!isMd} />
        )}

        {isSelected && (
          <span
            className={[
              "absolute bottom-1 right-1 w-4 h-4 rounded-full shadow-sm",
              "bg-white/95 flex items-center justify-center",
            ].join(" ")}
          >
            <CheckOutlined style={{ fontSize: 8, color: "#2563eb" }} />
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-1 px-0.5 min-w-0">
        <span
          className={[
            "font-semibold truncate",
            isMd ? "text-[12px]" : "text-[11px]",
            isSelected ? "text-white" : textColors.primary,
          ].join(" ")}
        >
          {label}
        </span>
        {variantCount > 1 && (
          <VariantCountBadge count={variantCount} isSelected={isSelected} />
        )}
      </div>

      {description && isMd && (
        <span
          className={[
            "mt-0.5 px-0.5 text-[10px] leading-snug line-clamp-2",
            isSelected ? "text-white/70" : textColors.muted,
          ].join(" ")}
        >
          {description}
        </span>
      )}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Manual layout tile
// ---------------------------------------------------------------------------
//
// The picker's gallery used to contain only automatic hero modes; the
// choice between Manual and Automatic layout lived in a separate
// "Layout Mode" toggle above it. That toggle is gone — Manual is now
// just another tile in the same "All layouts" gallery, deliberately
// rendered LAST (after every automatic family). It doesn't map to a
// ``hero_mode``; selecting it flips the hero into manual column-grid
// territory (``manual_layout = true``) via ``onSelectManual``.
//
// The tile mirrors ``HeroModeTile``'s shell so it reads as a peer of
// the automatic options, but draws a bespoke "columns" glyph instead
// of a mode preview to signal "you arrange this yourself".

function ManualLayoutPreview() {
  return (
    <svg
      viewBox="0 0 60 40"
      className="w-full h-full"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <rect x="4" y="6" width="15" height="28" rx="2" className="fill-slate-400/70 dark:fill-slate-500/70" />
      <rect x="22.5" y="6" width="15" height="28" rx="2" className="fill-slate-400/70 dark:fill-slate-500/70" />
      <rect x="41" y="6" width="15" height="28" rx="2" className="fill-slate-400/70 dark:fill-slate-500/70" />
    </svg>
  );
}

const ManualLayoutTile = React.memo(function ManualLayoutTile({
  isSelected,
  onClick,
  size = "md",
}) {
  const isMd = size === "md";
  return (
    <button
      type="button"
      onClick={onClick}
      title="Manual — build a custom column grid"
      aria-pressed={isSelected}
      data-testid="hero-mode-manual-tile"
      className={[
        "group relative flex flex-col items-stretch text-left",
        "rounded-xl border transition-all overflow-hidden",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
        isMd ? "p-2" : "p-1.5",
        isSelected
          ? `${interactive.selectedBg} ${interactive.selectedBorder} shadow-md`
          : `${borders.default} ${surfaces.tertiary} ${surfaces.hover}`,
      ].join(" ")}
    >
      <div
        className={[
          "relative w-full rounded-md overflow-hidden",
          isMd ? "aspect-[3/2] mb-2" : "aspect-[3/2] mb-1.5",
          isSelected
            ? "bg-gradient-to-b from-blue-500/10 to-blue-500/30"
            : "bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900",
        ].join(" ")}
      >
        <ManualLayoutPreview />

        {isSelected && (
          <span
            className={[
              "absolute bottom-1 right-1 w-4 h-4 rounded-full shadow-sm",
              "bg-white/95 flex items-center justify-center",
            ].join(" ")}
          >
            <CheckOutlined style={{ fontSize: 8, color: "#2563eb" }} />
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-1 px-0.5 min-w-0">
        <span
          className={[
            "font-semibold truncate",
            isMd ? "text-[12px]" : "text-[11px]",
            isSelected ? "text-white" : textColors.primary,
          ].join(" ")}
        >
          Manual
        </span>
      </div>

      {isMd && (
        <span
          className={[
            "mt-0.5 px-0.5 text-[10px] leading-snug line-clamp-2",
            isSelected ? "text-white/70" : textColors.muted,
          ].join(" ")}
        >
          Build a custom column grid you control per breakpoint.
        </span>
      )}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Active family panel (top section)
// ---------------------------------------------------------------------------
//
// Renders the currently-active family's name + description as a
// header, followed by a horizontal-wrap row of every variant in
// that family. The variant currently selected has the standard
// "selected" styling (ring + check + blue surface); other variants
// are quick-switch targets — a single click on any of them calls
// ``onChange(variant.id)``.
//
// For singleton families this section still renders, just with one
// tile inside. That keeps the layout shape consistent — users always
// see the same "active family on top, others below" structure
// regardless of whether the active family has variants.

function ActiveFamilyPanel({
  family,
  modesById,
  currentValue,
  onSelectVariant,
}) {
  const variants = useMemo(
    () =>
      family.members
        .map((id) => modesById[id])
        .filter(Boolean),
    [family.members, modesById]
  );

  if (variants.length === 0) return null;

  return (
    <section
      className={[
        "rounded-xl border p-3",
        // Slightly elevated surface (panel sits on the field
        // background) to read as "this is the active selection".
        // Stronger ring than the bottom-section cards so the eye
        // lands here first.
        surfaces.tertiary,
        borders.default,
        "ring-1 ring-blue-300/60 dark:ring-blue-500/40",
      ].join(" ")}
      data-testid="hero-mode-active-family-panel"
      data-family-id={family.id}
      role="group"
      aria-label={`${family.label} variants`}
    >
      {/* Single-line header: "SELECTED FAMILY · <Label>". The
          per-family description used to follow the label here but
          was removed — the family tiles in the grid below already
          carry their own short copy and the variant tiles below
          this header speak for themselves, so the extra prose only
          added visual noise to an already-busy panel. Both segments
          carry ``whitespace-nowrap`` so the conventionally-short
          label segments ("Selected family" and the family name)
          never wrap mid-word — when they did, "FAMILY" used to
          break onto its own line which looked like a layout
          glitch. */}
      <div className="flex items-baseline gap-2 mb-2 px-0.5 min-w-0">
        <span
          className={[
            "text-[11px] font-bold uppercase tracking-wide whitespace-nowrap",
            textColors.tertiary,
          ].join(" ")}
        >
          Selected family
        </span>
        <span
          className={`text-[12px] font-semibold whitespace-nowrap ${textColors.primary}`}
        >
          {family.label}
        </span>
      </div>

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))" }}
      >
        {variants.map((mode) => {
          const isSelected = currentValue === mode.id;
          const isAnimated = modeHasMotion(mode);
          return (
            <HeroModeTile
              key={mode.id}
              modeId={mode.id}
              label={mode.label}
              description={mode.description}
              isSelected={isSelected}
              showAnimatedChip={isAnimated}
              variantCount={1}
              onClick={() => onSelectVariant(mode.id)}
              title={mode.description}
              size="sm"
            />
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// All families grid (bottom section)
// ---------------------------------------------------------------------------
//
// Renders a scrollable grid of family cards for every family —
// including the currently-active one — so the visitor always sees
// the complete family taxonomy in one consistent place rather
// than having the active family disappear from the catalogue the
// moment they pick something. Two card flavours coexist in the
// grid:
//
//   * **Active-family card** — preview shows the user's
//     CURRENTLY-SELECTED variant (not the family default), the
//     card carries the selected styling (ring + check), and
//     clicking it is idempotent (calls ``onSelectVariant`` with
//     ``currentValue`` — same no-op the active panel does when
//     the user re-clicks the already-selected variant).
//
//     Why preview the current variant here instead of the family
//     default: the bottom grid would otherwise lie about the
//     user's choice — they'd see "Cinematic Right" highlighted
//     in the active panel above and "Cinematic" (the default)
//     shown in the bottom grid for the same family. Surfacing
//     the actual chosen variant keeps both views in sync.
//
//   * **Inactive-family cards** — preview shows
//     ``default_member`` (the family's canonical visual
//     identity), card is unselected, and clicking it auto-selects
//     the family's ``default_member`` (the "auto-select on
//     family click" UX).
//
// Internal scroll: the wrapper has ``max-h`` so users editing in a
// narrow side panel don't have to scroll the whole inspector to
// reach a family at the bottom of the list. The active-family
// panel (variant row) stays pinned above this scroll region.

function OtherFamiliesGrid({
  families,
  modesById,
  activeFamily,
  currentValue,
  selectionActive = true,
  onSelectFamily,
  onSelectVariant,
  manualLayout = false,
  onSelectManual,
  testId = "hero-mode-other-families-grid",
}) {
  if (families.length === 0) return null;

  return (
    <section className="flex flex-col" aria-label="All layouts">
      <div className="flex items-baseline justify-between mb-1 px-0.5">
        <span
          className={[
            "text-[11px] font-bold uppercase tracking-wide",
            textColors.tertiary,
          ].join(" ")}
        >
          All layouts
        </span>
        <span className={`text-[10px] ${textColors.muted}`}>
          {families.length} total
        </span>
      </div>

      <div
        // Scroll region. ``max-h-[32rem]`` ≈ 512px fits roughly three
        // rows of cards at the standard ~140px tile width — enough
        // to preview more of the catalogue at once without dominating
        // the inspector. Users scroll inside this region; the
        // active-family panel above stays pinned.
        className={[
          "max-h-[32rem] overflow-y-auto pr-1",
          // Subtle inner shadow / border to telegraph "this is its
          // own scroll container" so the user knows where to grab.
          "rounded-lg",
        ].join(" ")}
        data-testid={testId}
      >
        <div
          className="grid gap-2"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          }}
        >
          {families.map((family) => {
            const isActiveFamily =
              activeFamily != null && family.id === activeFamily.id;
            // Active-family card previews the actual chosen
            // variant; everyone else previews their default
            // member. ``modeId`` falls through to the default
            // when the active family's currentValue isn't a
            // known mode (defensive — should never happen
            // because the parent only passes ``activeFamily``
            // when ``currentValue`` is a registered member).
            const previewModeId = isActiveFamily
              ? currentValue || family.default_member
              : family.default_member;
            const previewMode = modesById[previewModeId];

            // Bottom cards aggregate the family's "does it move?"
            // signal: chip on if the displayed mode OR any member
            // is animated. This way users spot motion-bearing
            // families without expanding them.
            const previewIsAnimated = modeHasMotion(previewMode);
            const anyMemberIsAnimated = family.members.some((id) =>
              modeHasMotion(modesById[id])
            );
            const showAnimatedChip = previewIsAnimated || anyMemberIsAnimated;

            // Click target diverges by card flavour:
            //   * Active family → idempotent re-select of the
            //     current variant (no destructive default-jump).
            //     Mirrors the "click your already-selected
            //     variant in the active panel" behaviour pinned
            //     by the existing idempotency test, so both
            //     surfaces share one mental model.
            //   * Inactive family → auto-select its default
            //     member (the long-standing "auto-select on
            //     family click" rule).
            const handleClick = isActiveFamily
              ? () => onSelectVariant(currentValue || family.default_member)
              : () => onSelectFamily(family);

            return (
              <HeroModeTile
                key={family.id}
                modeId={previewModeId}
                label={family.label}
                description={family.description}
                // Highlight the featured family ONLY in automatic mode — in
                // Manual the sole "selected" tile is the Manual one.
                isSelected={isActiveFamily && selectionActive}
                showAnimatedChip={showAnimatedChip}
                variantCount={family.members.length}
                onClick={handleClick}
                title={family.description}
                size="md"
              />
            );
          })}
          {/* Manual layout always sits at the very end of the gallery. */}
          {typeof onSelectManual === "function" && (
            <ManualLayoutTile
              isSelected={!!manualLayout}
              onClick={onSelectManual}
              size="md"
            />
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Initial-state grid (no current value)
// ---------------------------------------------------------------------------
//
// When no hero mode has been picked yet (``currentValue == null``),
// there's no "active family" to pin at the top — showing one would
// imply a server-side selection that doesn't exist. We collapse the
// picker to a single grid of family cards instead. The first click
// auto-selects the family's ``default_member`` and the next render
// switches to the two-section layout naturally.
//
// We render this through ``OtherFamiliesGrid`` (without the scroll
// cap) so the visual identity matches the bottom section and we
// don't maintain two parallel grids.

function InitialFamiliesGrid({
  families,
  modesById,
  onSelectFamily,
  manualLayout = false,
  onSelectManual,
}) {
  return (
    <div data-testid="hero-mode-family-grid">
      <div
        className="grid gap-2"
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        }}
      >
        {families.map((family) => {
          const previewMode = modesById[family.default_member];
          const previewIsAnimated = modeHasMotion(previewMode);
          const anyMemberIsAnimated = family.members.some((id) =>
            modeHasMotion(modesById[id])
          );
          const showAnimatedChip = previewIsAnimated || anyMemberIsAnimated;

          return (
            <HeroModeTile
              key={family.id}
              modeId={family.default_member}
              label={family.label}
              description={family.description}
              isSelected={false}
              showAnimatedChip={showAnimatedChip}
              variantCount={family.members.length}
              onClick={() => onSelectFamily(family)}
              title={family.description}
              size="md"
            />
          );
        })}
        {/* Manual layout always sits at the very end of the gallery. */}
        {typeof onSelectManual === "function" && (
          <ManualLayoutTile
            isSelected={!!manualLayout}
            onClick={onSelectManual}
            size="md"
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Family-grouped picker
// ---------------------------------------------------------------------------
//
// Two-section layout:
//   * If currentValue resolves to a known family → render the
//     active panel on top and the rest as a scrollable grid below.
//   * If currentValue is missing or unknown → render the initial
//     family grid; the first click promotes that family to the top.

function FamilyGroupedPicker({
  modes,
  families,
  currentValue,
  onChange,
  manualLayout = false,
  onSelectManual,
}) {
  const modesById = useMemo(() => {
    const map = {};
    modes.forEach((m) => {
      map[m.id] = m;
    });
    return map;
  }, [modes]);

  // The featured family is derived from the (possibly dormant) ``hero_mode``
  // value in BOTH layout modes, so the active-family panel + "All layouts"
  // grid stay visible even while Manual is the active selection. (Only the
  // slot manager is hidden in Manual — that gating lives in ``EditField``.)
  const activeFamily = useMemo(() => {
    if (!currentValue) return null;
    return families.find((f) => f.members.includes(currentValue)) || null;
  }, [currentValue, families]);

  // Selection highlight. While Manual owns the selection no automatic tile is
  // drawn as selected (only the Manual tile is) — but the featured family
  // still renders so the user can see (and one-click return to) the layout
  // they had. Picking any automatic tile flips ``manual_layout`` back off.
  const selectedValue = manualLayout ? null : currentValue;
  const selectionActive = !manualLayout;

  // The bottom grid lists EVERY family — including the currently
  // active one — so the catalogue stays complete and the user
  // never sees a family vanish from the picker just because
  // they've selected something inside it. The active card is
  // visually distinguished there (selected styling, current
  // variant preview) and is non-destructive on click; see
  // ``OtherFamiliesGrid`` for the per-card branching.
  //
  // Initial-state grid is unchanged (no ``activeFamily`` yet, so
  // there's nothing to dedupe — every card shows the family
  // default).
  const allFamilies = families;

  const handleSelectVariant = useCallback(
    (modeId) => {
      onChange(modeId);
    },
    [onChange]
  );

  // Activating a family from the bottom grid auto-selects its
  // declared default member. Singletons collapse to that single
  // member; multi-member families land on the backend-declared
  // default (e.g. left-anchored Split, animated Gallery), which is
  // the product choice when the user hasn't picked a variant yet.
  const handleSelectFamily = useCallback(
    (family) => {
      onChange(family.default_member);
    },
    [onChange]
  );

  // Initial state (no automatic mode selected, or Manual active): single
  // grid, no active panel. Avoids implying an automatic selection when
  // there isn't one. The Manual tile rides along at the end and carries
  // the selected styling whenever Manual is active.
  if (!activeFamily) {
    return (
      <InitialFamiliesGrid
        families={families}
        modesById={modesById}
        onSelectFamily={handleSelectFamily}
        manualLayout={manualLayout}
        onSelectManual={onSelectManual}
      />
    );
  }

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="hero-mode-family-grid"
    >
      <ActiveFamilyPanel
        family={activeFamily}
        modesById={modesById}
        currentValue={selectedValue}
        onSelectVariant={handleSelectVariant}
      />
      <OtherFamiliesGrid
        families={allFamilies}
        modesById={modesById}
        activeFamily={activeFamily}
        currentValue={currentValue}
        selectionActive={selectionActive}
        onSelectFamily={handleSelectFamily}
        onSelectVariant={handleSelectVariant}
        manualLayout={manualLayout}
        onSelectManual={onSelectManual}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fallback: flat picker (legacy backend missing ``families`` payload)
// ---------------------------------------------------------------------------
//
// If a deployed backend hasn't shipped the families taxonomy yet (or
// a unit test mocks the metadata without ``families``), we degrade
// gracefully to a flat one-tile-per-mode grid. The visual style
// matches the new family tiles so the upgrade is invisible to the
// user the moment the API catches up.

function FlatPicker({ modes, currentValue, onChange, manualLayout = false, onSelectManual }) {
  const effectiveValue = manualLayout ? null : currentValue;
  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}
      data-testid="hero-mode-flat-grid"
    >
      {modes.map((mode) => {
        const isSelected = effectiveValue === mode.id;
        const isAnimated = modeHasMotion(mode);
        return (
          <HeroModeTile
            key={mode.id}
            modeId={mode.id}
            label={mode.label}
            description={mode.description}
            isSelected={isSelected}
            showAnimatedChip={isAnimated}
            variantCount={1}
            onClick={() => onChange(mode.id)}
            title={mode.description}
            size="md"
          />
        );
      })}
      {/* Manual layout always sits at the very end of the gallery. */}
      {typeof onSelectManual === "function" && (
        <ManualLayoutTile
          isSelected={!!manualLayout}
          onClick={onSelectManual}
          size="md"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level field component
// ---------------------------------------------------------------------------

export default function HeroModeField({
  label,
  help,
  value,
  onChange,
  infoText,
  manualLayout = false,
  onSelectManual,
}) {
  const { heroModes } = useMetadata();

  const modes = useMemo(() => heroModes?.modes || [], [heroModes]);
  const families = useMemo(() => heroModes?.families || [], [heroModes]);

  const currentValue = value || null;

  if (!modes.length) {
    return (
      <div className="field-wrapper">
        {label && (
          <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
            {label}
          </label>
        )}
        <div className={`text-center py-4 ${typography.caption} ${textColors.muted}`}>
          Loading hero modes...
        </div>
      </div>
    );
  }

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? (
            <span className="ml-2">
              <InfoIcon text={infoText} />
            </span>
          ) : null}
        </label>
      )}

      {families.length > 0 ? (
        <FamilyGroupedPicker
          modes={modes}
          families={families}
          currentValue={currentValue}
          onChange={onChange}
          manualLayout={manualLayout}
          onSelectManual={onSelectManual}
        />
      ) : (
        <FlatPicker
          modes={modes}
          currentValue={currentValue}
          onChange={onChange}
          manualLayout={manualLayout}
          onSelectManual={onSelectManual}
        />
      )}

      {help && (
        <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
          {help}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Test exports
// ---------------------------------------------------------------------------
//
// Internal components are exported for unit testing without exposing
// them as part of the field's public API. Production consumers should
// only use the default export.

export {
  HeroModeTile as __HeroModeTile,
  ManualLayoutTile as __ManualLayoutTile,
  ActiveFamilyPanel as __ActiveFamilyPanel,
  OtherFamiliesGrid as __OtherFamiliesGrid,
  InitialFamiliesGrid as __InitialFamiliesGrid,
  FamilyGroupedPicker as __FamilyGroupedPicker,
  FlatPicker as __FlatPicker,
  AnimatedChip as __AnimatedChip,
  VariantCountBadge as __VariantCountBadge,
};
